// The native calendar (CALENDAR-C1; docs/design/2026-10-28-calendar.md §2–§7). Calendars of three
// kinds (the company's, a team's, a person's), events with an RRULE on the master and exception
// rows for "this event" and "this and following" edits, attendees and their responses, shares
// beyond the default, the person's overlay (follows), each event's history, the tasks due in a
// window (read-only chips), agent changes held for approval, and the person's read-only ICS feed.
//
// Who sees what (§2.2): the company calendar is everyone's in full; a team's is its members' in
// full and busy blocks to others; a person's is busy blocks to everyone else (managers and admins
// included) unless the owner shares it or opens one event; `hidden` shows nothing. Attending an
// event shows it at the event's own visibility. Who edits (§2.3): a calendar's writers, and the
// event's organizer wherever it lives; attendees answer only; a meeting's projected event is
// edited through the meeting. A busy block carries no title, attendees or location.
import { z } from "zod";
import {
  Calendar,
  CalendarEvent,
  CalendarEventAttendee,
  CalendarEventRevision,
  CalendarShare,
} from "../calendar-rows";
import {
  CalendarAttendeeResponse,
  CalendarEventSource,
  CalendarEventStatus,
  CalendarEventVisibility,
  CalendarShareGrantee,
  CalendarShareLevel,
  CalendarVisibility,
} from "../enums";
import { Email, LIMITS, Timestamp, TimeZone, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

const Title = z.string().trim().min(1).max(500);
const Description = z.string().max(LIMITS.longText);
const Location = z.string().trim().max(500);
const Color = z.string().trim().min(1).max(32);
const RRule = z.string().trim().min(1).max(500);

/** What the caller may do with a calendar's events: see busy blocks, read details, write. */
export const CalendarAccess = z.enum(["busy", "read", "write"]).meta({ id: "CalendarAccess" });
export type CalendarAccess = z.infer<typeof CalendarAccess>;

/** Edit scopes of a recurring event (§2.3): one occurrence, it and the later ones, the series. */
export const CalendarEditScope = z
  .enum(["this", "following", "all"])
  .meta({ id: "CalendarEditScope" });
export type CalendarEditScope = z.infer<typeof CalendarEditScope>;

// Calendars -------------------------------------------------------------------------------------

/**
 * A calendar as the overlay list shows it: the row, its names, the caller's access, whether the
 * caller manages it (settings and shares: the company's managers, a team's lead, a person), and
 * the caller's follow (`default_follow`: the company, the caller's teams and the caller's own
 * are always in the list; they can be hidden, not unfollowed).
 */
export const CalendarView = Calendar.extend({
  team_name: z.string().nullable(),
  owner_name: z.string().nullable(),
  access: CalendarAccess,
  manages: z.boolean(),
  followed: z.boolean(),
  default_follow: z.boolean(),
  shown: z.boolean(),
  /** The follow's colour override, else the calendar's colour. */
  display_color: z.string().nullable(),
  /** CAL-WORK-LAYERS: a work calendar's board (null for every other kind). */
  board_id: Uuid.nullable(),
  board_name: z.string().nullable(),
}).meta({ id: "CalendarView" });
export type CalendarView = z.infer<typeof CalendarView>;

export const CalendarList = z
  .object({
    data: z.array(CalendarView),
    /** The caller's own calendar (null for an agent, which has none). */
    my_calendar_id: Uuid.nullable(),
    my_membership_id: Uuid.nullable(),
    /** The company's zone and week start (1 = Monday), for the views' axes. */
    timezone: z.string(),
    week_start: z.int().min(1).max(7),
  })
  .meta({ id: "CalendarList" });
export type CalendarList = z.infer<typeof CalendarList>;

export const CalendarPatch = z
  .strictObject({
    name: z.string().trim().min(1).max(200).optional(),
    color: Color.nullable().optional(),
    /** What people outside it see by default (a person's calendar: busy unless opened). */
    visibility: CalendarVisibility.optional(),
    /** Null: the company's zone. */
    timezone: TimeZone.nullable().optional(),
  })
  .meta({ id: "CalendarPatch" });

/**
 * CAL-WORK-LAYERS: publish a board's calendar view into the Calendar as a work calendar (one per
 * view; the caller edits the board and becomes its owner). Name default: the board's name.
 */
export const CalendarPublish = z
  .strictObject({
    view_id: Uuid,
    name: z.string().trim().min(1).max(200).optional(),
    color: Color.nullable().optional(),
  })
  .meta({ id: "CalendarPublish" });

// Layers (work calendars and milestones) ---------------------------------------------------------

/**
 * One chip on a layer calendar: a task on a work calendar (on the view's date), or a milestone
 * (a goal's or a priority's due date, a goal milestone). `day` is the calendar day; `at` the
 * instant when the date is one (due, start), null for a date field's plain day. Work chips carry
 * the task's `version` and the view's `date_field` so a drag writes the right date; `can_edit`
 * is the task's edit right. Milestones are read-only.
 */
export const CalendarItem = z
  .object({
    key: z.string(),
    calendar_id: Uuid,
    kind: z.enum(["task", "milestone"]),
    /** The task's id, or the goal's (a milestone opens its goal). */
    ref_id: Uuid,
    title: z.string(),
    day: z.string(),
    at: Timestamp.nullable(),
    done: z.boolean(),
    can_edit: z.boolean(),
    board_id: Uuid.nullable(),
    date_field: z.string().nullable(),
    version: z.int().min(1).nullable(),
    status_label: z.string().nullable(),
    status_color: z.string().nullable(),
    /** A milestone's seat ("CEO") and what it is: goal, priority, annual, milestone, target. */
    seat_title: z.string().nullable(),
    milestone_kind: z.enum(["goal", "priority", "annual", "milestone", "target"]).nullable(),
  })
  .meta({ id: "CalendarItem" });
export type CalendarItem = z.infer<typeof CalendarItem>;

export const CalendarItemList = z
  .object({ data: z.array(CalendarItem), truncated: z.boolean() })
  .meta({ id: "CalendarItemList" });

// Shares ----------------------------------------------------------------------------------------

export const CalendarShareView = CalendarShare.extend({
  grantee_name: z.string().nullable(),
}).meta({ id: "CalendarShareView" });
export type CalendarShareView = z.infer<typeof CalendarShareView>;

export const CalendarShareList = z
  .object({ data: z.array(CalendarShareView) })
  .meta({ id: "CalendarShareList" });

/**
 * A share (a live one to the same grantee changes level). `grantee_id` is omitted for `company`.
 * A work calendar takes `read` only (it holds no events to write).
 */
export const CalendarShareCreate = z
  .strictObject({
    grantee_kind: CalendarShareGrantee,
    grantee_id: Uuid.optional(),
    level: CalendarShareLevel,
  })
  .meta({ id: "CalendarShareCreate" });

export const CalendarShareParams = z
  .object({ co: Uuid, id: Uuid, share: Uuid })
  .meta({ id: "CalendarShareParams" });

// Follows ---------------------------------------------------------------------------------------

/** Follow a calendar (by id) or a person's calendar (by membership; made on first follow). */
export const CalendarFollowCreate = z
  .strictObject({
    calendar_id: Uuid.optional(),
    membership_id: Uuid.optional(),
    shown: z.boolean().optional(),
    color: Color.nullable().optional(),
  })
  .meta({ id: "CalendarFollowCreate" });

export const CalendarFollowPatch = z
  .strictObject({
    shown: z.boolean().optional(),
    color: Color.nullable().optional(),
  })
  .meta({ id: "CalendarFollowPatch" });

// Events ----------------------------------------------------------------------------------------

export const CalendarWindowQuery = z
  .object({
    from: Timestamp,
    to: Timestamp,
    /** Comma-separated calendar ids; default: the caller's followed calendars that are shown. */
    calendar_ids: z.string().max(4000).optional(),
    /** Events the caller attends on calendars it does not follow (default true). */
    attending: z.enum(["true", "false"]).optional(),
  })
  .meta({ id: "CalendarWindowQuery" });

/**
 * One occurrence in a window. A recurring series is expanded here (`series_id` set, the
 * occurrence's slot in `original_start_at`); an exception replaces its slot. `busy`: the caller
 * sees a block only (title, location, organizer and attendees are null). `attending`: shown
 * because the caller attends it, not because it follows the calendar.
 */
export const CalendarOccurrence = z
  .object({
    key: z.string(),
    event_id: Uuid,
    series_id: Uuid.nullable(),
    calendar_id: Uuid,
    starts_at: Timestamp,
    ends_at: Timestamp,
    all_day: z.boolean(),
    timezone: z.string(),
    original_start_at: Timestamp.nullable(),
    recurring: z.boolean(),
    busy: z.boolean(),
    title: z.string().nullable(),
    location: z.string().nullable(),
    status: CalendarEventStatus,
    source: CalendarEventSource,
    source_ref: Uuid.nullable(),
    organizer_membership_id: Uuid.nullable(),
    organizer_name: z.string().nullable(),
    /** Whose busy block it is ("Busy · Valentina"): the calendar's person or team. */
    owner_name: z.string().nullable(),
    attendee_count: z.int().min(0).nullable(),
    my_response: CalendarAttendeeResponse.nullable(),
    attending: z.boolean(),
    can_edit: z.boolean(),
    can_rsvp: z.boolean(),
  })
  .meta({ id: "CalendarOccurrence" });
export type CalendarOccurrence = z.infer<typeof CalendarOccurrence>;

export const CalendarOccurrenceList = z
  .object({
    data: z.array(CalendarOccurrence),
    from: Timestamp,
    to: Timestamp,
    /** More than the cap (2000 occurrences) fell in the window. */
    truncated: z.boolean(),
  })
  .meta({ id: "CalendarOccurrenceList" });

export const CalendarAttendeeView = CalendarEventAttendee.extend({
  name: z.string().nullable(),
  /** `person` or `digital_employee` for a member; null for an outside email. */
  member_kind: z.string().nullable(),
}).meta({ id: "CalendarAttendeeView" });
export type CalendarAttendeeView = z.infer<typeof CalendarAttendeeView>;

/**
 * An event in full (details only; a busy-only event is 403). For an occurrence of a series
 * (`?occurrence_start=`) the times are the occurrence's and the rest the master's or the
 * exception's.
 */
export const CalendarEventDetail = CalendarEvent.extend({
  calendar_name: z.string(),
  calendar_kind: z.string(),
  attendees: z.array(CalendarAttendeeView),
  organizer_name: z.string().nullable(),
  /** The series' rule when this is an exception or an occurrence. */
  series_rrule: z.string().nullable(),
  series_id: Uuid.nullable(),
  /**
   * The series' (master's) version: the `version` a "following" or "all" edit or cancel sends;
   * `version` itself is the occurrence's (its exception's, else the master's), for "this".
   */
  series_version: z.int().min(1).nullable(),
  occurrence_start: Timestamp.nullable(),
  my_response: CalendarAttendeeResponse.nullable(),
  can_edit: z.boolean(),
  can_rsvp: z.boolean(),
  /** Why it cannot be edited here ("Edited through the meeting"), when it cannot. */
  read_only_reason: z.string().nullable(),
  revisions: z.int().min(0),
}).meta({ id: "CalendarEventDetail" });
export type CalendarEventDetail = z.infer<typeof CalendarEventDetail>;

export const CalendarEventGetQuery = z
  .object({ occurrence_start: Timestamp.optional() })
  .meta({ id: "CalendarEventGetQuery" });

export const CalendarAttendeeInput = z
  .union([
    z.strictObject({ membership_id: Uuid, optional: z.boolean().optional() }),
    z.strictObject({ email: Email, optional: z.boolean().optional() }),
  ])
  .meta({ id: "CalendarAttendeeInput" });

/**
 * A new event. All-day events take their dates from `starts_at` and `ends_at` in `timezone`
 * (the end exclusive; the same day means one day). `rrule` makes it a series (RFC 5545 subset of
 * the M1-05 engine, no DTSTART; its time of day is the start's).
 */
export const CalendarEventCreate = z
  .strictObject({
    calendar_id: Uuid,
    title: Title,
    description: Description.nullable().optional(),
    location: Location.nullable().optional(),
    starts_at: Timestamp,
    ends_at: Timestamp,
    all_day: z.boolean().optional(),
    /** Default: the calendar's zone, else the company's. */
    timezone: TimeZone.optional(),
    status: z.enum(["confirmed", "tentative"]).optional(),
    visibility: CalendarEventVisibility.optional(),
    rrule: RRule.nullable().optional(),
    attendees: z.array(CalendarAttendeeInput).max(LIMITS.list).optional(),
  })
  .meta({ id: "CalendarEventCreate" });
export type CalendarEventCreate = z.infer<typeof CalendarEventCreate>;

/**
 * An edit. On a series `scope` is required (`this`, `following` need `occurrence_start`, the
 * slot of the occurrence edited); `starts_at` / `ends_at` are then the occurrence's new times
 * and move the series by the same offset. `attendees` replaces the list. `version` (the one
 * read) makes a stale edit 409.
 */
export const CalendarEventPatch = z
  .strictObject({
    scope: CalendarEditScope.optional(),
    occurrence_start: Timestamp.optional(),
    version: z.int().min(1).optional(),
    calendar_id: Uuid.optional(),
    title: Title.optional(),
    description: Description.nullable().optional(),
    location: Location.nullable().optional(),
    starts_at: Timestamp.optional(),
    ends_at: Timestamp.optional(),
    all_day: z.boolean().optional(),
    timezone: TimeZone.optional(),
    status: z.enum(["confirmed", "tentative"]).optional(),
    visibility: CalendarEventVisibility.optional(),
    rrule: RRule.nullable().optional(),
    attendees: z.array(CalendarAttendeeInput).max(LIMITS.list).optional(),
  })
  .meta({ id: "CalendarEventPatch" });
export type CalendarEventPatch = z.infer<typeof CalendarEventPatch>;

export const CalendarEventDeleteQuery = z
  .object({
    scope: CalendarEditScope.optional(),
    occurrence_start: Timestamp.optional(),
    /** The version read (409 when the event changed since). */
    version: z.coerce.number().int().min(1).optional(),
  })
  .meta({ id: "CalendarEventDeleteQuery" });

export const CalendarRsvp = z
  .strictObject({
    response: z.enum(["accepted", "declined", "tentative"]),
    /** On a series: `this` answers one occurrence (with `occurrence_start`), `all` the series. */
    scope: z.enum(["this", "all"]).optional(),
    occurrence_start: Timestamp.optional(),
  })
  .meta({ id: "CalendarRsvp" });

export const CalendarRevisionList = z
  .object({ data: z.array(CalendarEventRevision) })
  .meta({ id: "CalendarRevisionList" });

// Tasks overlay ---------------------------------------------------------------------------------

/** A task due in the window, as a read-only chip (its due date changes in Work). */
export const CalendarTaskChip = z
  .object({
    id: Uuid,
    title: z.string(),
    due_at: Timestamp,
    board_id: Uuid,
    board_name: z.string().nullable(),
    done: z.boolean(),
    overdue: z.boolean(),
  })
  .meta({ id: "CalendarTaskChip" });
export type CalendarTaskChip = z.infer<typeof CalendarTaskChip>;

export const CalendarTaskQuery = z
  .object({ from: Timestamp, to: Timestamp })
  .meta({ id: "CalendarTaskQuery" });

export const CalendarTaskList = z
  .object({ data: z.array(CalendarTaskChip) })
  .meta({ id: "CalendarTaskList" });

// Agent changes (approval) ----------------------------------------------------------------------

/**
 * A change an agent asks for (§6), or a person's "Suggest an event" on a calendar they cannot
 * write: create, update or cancel an event. Applied at once when the caller may write directly
 * (a person with write; an owner's assistant with `calendar` autonomy `alone` on the owner's own
 * calendar), else held as a `calendar_change` approval for the calendar's writers.
 */
export const CalendarChangePayload = z
  .discriminatedUnion("op", [
    z.strictObject({ op: z.literal("create"), event: CalendarEventCreate }),
    z.strictObject({ op: z.literal("update"), event_id: Uuid, patch: CalendarEventPatch }),
    z.strictObject({
      op: z.literal("cancel"),
      event_id: Uuid,
      scope: CalendarEditScope.optional(),
      occurrence_start: Timestamp.optional(),
      /** The version read; a held proposal binds the current one when none is given. */
      version: z.int().min(1).optional(),
    }),
  ])
  .meta({ id: "CalendarChangePayload" });
export type CalendarChangePayload = z.infer<typeof CalendarChangePayload>;

export const CalendarChangeCreate = z
  .strictObject({
    change: CalendarChangePayload,
    /** One line for the approver ("Add the Q4 webinar to the company calendar"). */
    summary: z.string().trim().min(1).max(500).optional(),
  })
  .meta({ id: "CalendarChangeCreate" });

export const CalendarChangeResult = z
  .object({
    outcome: z.enum(["applied", "pending"]),
    event_id: Uuid.nullable(),
    approval_id: Uuid.nullable(),
  })
  .meta({ id: "CalendarChangeResult" });

// ICS feed --------------------------------------------------------------------------------------

export const CalendarFeedStatus = z
  .object({
    active: z.boolean(),
    created_at: Timestamp.nullable(),
    last_used_at: Timestamp.nullable(),
  })
  .meta({ id: "CalendarFeedStatus" });

/** The new link, shown once (the token is stored hashed). */
export const CalendarFeedLink = z
  .object({ token: z.string(), path: z.string(), created_at: Timestamp })
  .meta({ id: "CalendarFeedLink" });

export const CalendarFeedParams = z
  .object({ co: Uuid, token: z.string().min(20).max(200) })
  .meta({ id: "CalendarFeedParams" });

// Routes ----------------------------------------------------------------------------------------

const eventItem = `${base}/calendar/events/{id}`;
const calendarItem = `${base}/calendars/{id}`;

export const calendarRoutes = {
  "calendars.list": defineRoute({
    ...core,
    id: "calendars.list",
    method: "get",
    path: `${base}/calendars`,
    tag: "calendar",
    summary: "The calendars the caller sees, with its overlay (follows, shown, colours)",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: CalendarList },
    errors: [],
    emits: [],
  }),
  "calendars.update": defineRoute({
    ...core,
    id: "calendars.update",
    method: "patch",
    path: calendarItem,
    tag: "calendar",
    summary: "Rename a calendar, recolour it, set its default visibility or time zone",
    access: "viewer",
    params: CompanyItemParams,
    body: CalendarPatch,
    response: { status: 200, schema: CalendarView },
    errors: ["validation_failed"],
    emits: ["calendar.updated"],
  }),
  "calendars.publish": defineRoute({
    ...core,
    id: "calendars.publish",
    method: "post",
    path: `${base}/calendars`,
    tag: "calendar",
    summary: "Publish a board's calendar view into the Calendar as a work calendar",
    description:
      "Board editors. The caller owns it; everyone who can view the board sees it, plus its shares.",
    access: "viewer",
    params: CompanyParams,
    body: CalendarPublish,
    idempotency: "optional",
    response: { status: 201, schema: CalendarView },
    errors: ["validation_failed", "conflict"],
    emits: ["calendar.published"],
  }),
  "calendars.unpublish": defineRoute({
    ...core,
    id: "calendars.unpublish",
    method: "delete",
    path: calendarItem,
    tag: "calendar",
    summary: "Take a work calendar out of the Calendar (its owner, an admin or a board editor)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["calendar.unpublished"],
  }),
  "calendarItems.list": defineRoute({
    ...core,
    id: "calendarItems.list",
    method: "get",
    path: `${base}/calendar/items`,
    tag: "calendar",
    summary: "The chips of the caller's shown work calendars and Milestones in a window",
    description: "Tasks on their view's date and dated goals; never busy, never in the ICS feed.",
    access: "viewer",
    params: CompanyParams,
    query: CalendarWindowQuery,
    response: { status: 200, schema: CalendarItemList },
    errors: ["validation_failed"],
    emits: [],
  }),
  "calendarShares.list": defineRoute({
    ...core,
    id: "calendarShares.list",
    method: "get",
    path: `${calendarItem}/shares`,
    tag: "calendar",
    summary: "A calendar's live shares (its managers)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: CalendarShareList },
    errors: [],
    emits: [],
  }),
  "calendarShares.create": defineRoute({
    ...core,
    id: "calendarShares.create",
    method: "post",
    path: `${calendarItem}/shares`,
    tag: "calendar",
    summary: "Share a calendar with a member, a team or the company (busy, read or write)",
    access: "viewer",
    params: CompanyItemParams,
    body: CalendarShareCreate,
    idempotency: "optional",
    response: { status: 201, schema: CalendarShareView },
    errors: ["validation_failed"],
    emits: ["calendar.shared"],
  }),
  "calendarShares.delete": defineRoute({
    ...core,
    id: "calendarShares.delete",
    method: "delete",
    path: `${calendarItem}/shares/{share}`,
    tag: "calendar",
    summary: "Revoke a calendar share",
    access: "viewer",
    params: CalendarShareParams,
    response: { status: 204 },
    errors: [],
    emits: ["calendar.share_revoked"],
  }),
  "calendarFollows.create": defineRoute({
    ...core,
    id: "calendarFollows.create",
    method: "post",
    path: `${base}/calendar/follows`,
    tag: "calendar",
    summary: "Follow a calendar, or a person's calendar (busy blocks unless shared)",
    access: "viewer",
    params: CompanyParams,
    body: CalendarFollowCreate,
    response: { status: 200, schema: CalendarView },
    errors: ["validation_failed"],
    emits: ["calendar.follow_changed"],
  }),
  "calendarFollows.update": defineRoute({
    ...core,
    id: "calendarFollows.update",
    method: "patch",
    path: `${base}/calendar/follows/{id}`,
    tag: "calendar",
    summary: "Show, hide or recolour a calendar in the caller's overlay (the id is the calendar's)",
    access: "viewer",
    params: CompanyItemParams,
    body: CalendarFollowPatch,
    response: { status: 200, schema: CalendarView },
    errors: ["validation_failed"],
    emits: ["calendar.follow_changed"],
  }),
  "calendarFollows.delete": defineRoute({
    ...core,
    id: "calendarFollows.delete",
    method: "delete",
    path: `${base}/calendar/follows/{id}`,
    tag: "calendar",
    summary: "Unfollow a calendar (the company's, my teams' and my own can only be hidden)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["calendar.follow_changed"],
  }),
  "calendarEvents.list": defineRoute({
    ...core,
    id: "calendarEvents.list",
    method: "get",
    path: `${base}/calendar/events`,
    tag: "calendar",
    summary: "The occurrences in a window (at most 100 days) on the caller's calendars",
    access: "viewer",
    params: CompanyParams,
    query: CalendarWindowQuery,
    response: { status: 200, schema: CalendarOccurrenceList },
    errors: ["validation_failed"],
    emits: [],
  }),
  "calendarEvents.get": defineRoute({
    ...core,
    id: "calendarEvents.get",
    method: "get",
    path: eventItem,
    tag: "calendar",
    summary: "An event in full, or one occurrence of a series",
    access: "viewer",
    params: CompanyItemParams,
    query: CalendarEventGetQuery,
    response: { status: 200, schema: CalendarEventDetail },
    errors: [],
    emits: [],
  }),
  "calendarEvents.create": defineRoute({
    ...core,
    id: "calendarEvents.create",
    method: "post",
    path: `${base}/calendar/events`,
    tag: "calendar",
    summary: "Add an event (or a series) to a calendar the caller writes",
    access: "viewer",
    params: CompanyParams,
    body: CalendarEventCreate,
    idempotency: "optional",
    response: { status: 201, schema: CalendarEventDetail },
    errors: ["validation_failed"],
    emits: ["calendar_event.created"],
  }),
  "calendarEvents.update": defineRoute({
    ...core,
    id: "calendarEvents.update",
    method: "patch",
    path: eventItem,
    tag: "calendar",
    summary: "Edit an event: this occurrence, this and following, or the whole series",
    access: "viewer",
    params: CompanyItemParams,
    body: CalendarEventPatch,
    response: { status: 200, schema: CalendarEventDetail },
    errors: ["validation_failed", "conflict"],
    emits: ["calendar_event.updated", "calendar_event.created"],
  }),
  "calendarEvents.delete": defineRoute({
    ...core,
    id: "calendarEvents.delete",
    method: "delete",
    path: eventItem,
    tag: "calendar",
    summary: "Cancel an event: this occurrence, this and following, or the whole series",
    access: "viewer",
    params: CompanyItemParams,
    query: CalendarEventDeleteQuery,
    response: { status: 204 },
    errors: ["validation_failed", "conflict"],
    emits: ["calendar_event.cancelled"],
  }),
  "calendarEvents.rsvp": defineRoute({
    ...core,
    id: "calendarEvents.rsvp",
    method: "post",
    path: `${eventItem}/rsvp`,
    tag: "calendar",
    summary: "Answer an invitation (accept, decline, maybe): one occurrence or the series",
    access: "viewer",
    params: CompanyItemParams,
    body: CalendarRsvp,
    response: { status: 200, schema: CalendarEventDetail },
    errors: ["validation_failed"],
    emits: ["calendar_event.responded"],
  }),
  "calendarEvents.revisions": defineRoute({
    ...core,
    id: "calendarEvents.revisions",
    method: "get",
    path: `${eventItem}/revisions`,
    tag: "calendar",
    summary: "An event's history: the event as it was before each edit",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: CalendarRevisionList },
    errors: [],
    emits: [],
  }),
  "calendarTasks.list": defineRoute({
    ...core,
    id: "calendarTasks.list",
    method: "get",
    path: `${base}/calendar/tasks`,
    tag: "calendar",
    summary: "The caller's tasks due in a window, as read-only chips",
    access: "viewer",
    params: CompanyParams,
    query: CalendarTaskQuery,
    response: { status: 200, schema: CalendarTaskList },
    errors: ["validation_failed"],
    emits: [],
  }),
  "calendarChanges.create": defineRoute({
    ...core,
    id: "calendarChanges.create",
    method: "post",
    path: `${base}/calendar/changes`,
    tag: "calendar",
    summary: "Propose a calendar change: applied when the caller may write, else held for approval",
    access: "viewer",
    params: CompanyParams,
    body: CalendarChangeCreate,
    idempotency: "optional",
    response: { status: 200, schema: CalendarChangeResult },
    errors: ["validation_failed", "conflict"],
    emits: [
      "approval.requested",
      "calendar_event.created",
      "calendar_event.updated",
      "calendar_event.cancelled",
    ],
  }),
  "calendarFeed.get": defineRoute({
    ...core,
    id: "calendarFeed.get",
    method: "get",
    path: `${base}/calendar/feed`,
    tag: "calendar",
    summary: "Whether the caller has a live ICS feed link",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: CalendarFeedStatus },
    errors: [],
    emits: [],
  }),
  "calendarFeed.create": defineRoute({
    ...core,
    id: "calendarFeed.create",
    method: "post",
    path: `${base}/calendar/feed`,
    tag: "calendar",
    summary: "Make a new ICS feed link (the old one stops working); shown once",
    access: "viewer",
    params: CompanyParams,
    response: { status: 201, schema: CalendarFeedLink },
    errors: [],
    emits: ["calendar_feed.created"],
  }),
  "calendarFeed.delete": defineRoute({
    ...core,
    id: "calendarFeed.delete",
    method: "delete",
    path: `${base}/calendar/feed`,
    tag: "calendar",
    summary: "Revoke the caller's ICS feed link",
    access: "viewer",
    params: CompanyParams,
    response: { status: 204 },
    errors: [],
    emits: ["calendar_feed.revoked"],
  }),
  "calendarFeed.ics": defineRoute({
    ...core,
    id: "calendarFeed.ics",
    method: "get",
    path: `${base}/calendar/feed/{token}`,
    tag: "calendar",
    summary: "The ICS feed of what the link's person sees (90 days back, a year ahead)",
    description: "No principal: the token in the path is the credential (text/calendar).",
    access: "feed_token",
    params: CalendarFeedParams,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: ["conflict"],
    emits: [],
  }),
} as const;
